import type { BoxProps } from '@hover/blueprint';
import { Body, Box, Divider, Heading } from '@hover/blueprint';
import type { TFunction } from 'i18next';
import React, { useState } from 'react';
import { Trans } from 'react-i18next';
import { useHistory } from 'react-router';

import { useHomeownerAppContext } from 'src/modules/homeownerApp/hooks';
import type { AiImageEdits } from 'src/shared/graphql/generated';
import {
  TranslationNamespace,
  useLocation,
  useTranslate,
} from 'src/shared/hooks';
import { buttonPressed } from 'src/shared/telemetry';

import type { RegionWithDisabledRegions } from '../../FullscreenImage/useHumanReadableEdits';
import {
  getSnakecaseRegionFromKey,
  mapRegionToDisplayText,
} from '../../FullscreenImage/useHumanReadableEdits';
import type { NavItemSchema } from '../../Navbar';
import { NavbarButton } from '../../Navbar/NavbarButton';

const TRACK_ID = 'project-jump-list';

export type EditedRegions = { spaceType: string; edits: string[] }[];

interface ProjectDetailsProps extends BoxProps {
  name: string;
  navItems: string[];
  onNavButtonClick: (sectionName: string) => void;
  projectId: number;
  editedRegions: EditedRegions;
  navRef?: React.RefObject<HTMLDivElement>;
}

export type ProjectNavName = (typeof jumpLinks)[number]['name'];

const jumpLinks = [
  {
    name: 'measurements',
    trackId: TRACK_ID,
    path: '#measurements',
  },
  {
    name: 'materials',
    trackId: TRACK_ID,
    path: '#materials',
  },
  {
    name: 'services',
    trackId: TRACK_ID,
    path: '#services',
  },
  {
    name: 'help',
    trackId: TRACK_ID,
    path: '#help',
  },
] as const satisfies readonly NavItemSchema[];

const getAreaDescription = (editedRegions: EditedRegions, t: TFunction) => {
  const areas: string[] = editedRegions.map(({ spaceType }) => spaceType);
  const regions = Array.from(
    new Set(editedRegions.map(({ edits }) => edits).flat()),
  ).map(region =>
    mapRegionToDisplayText(
      t,
      getSnakecaseRegionFromKey(
        region as keyof AiImageEdits,
      ) as RegionWithDisabledRegions,
    ).toLowerCase(),
  );
  const area: string[] = [];
  const isPlural = areas.length > 1 || regions.length > 1;

  if (areas.includes('exterior'))
    area.push(t('home.aspects.exterior').toLowerCase());
  if (areas.some(item => item !== 'exterior'))
    area.push(t('home.aspects.interior').toLowerCase());
  if (area.length) area[0] = area[0][0].toUpperCase() + area[0].slice(1);

  return (
    <Trans
      i18nKey="projects.projectDescription"
      t={t}
      values={{
        area,
        regions,
        procedure: isPlural
          ? t('projects.procedure.updates')
          : t('projects.procedure.update'),
        style: 'long', // https://www.i18next.com/translation-function/formatting#list
      }}
    />
  );
};

const ProjectDetails = ({
  name,
  navItems,
  onNavButtonClick,
  projectId,
  editedRegions,
  px,
  navRef,
  ...rest
}: ProjectDetailsProps) => {
  const location = useLocation();
  const history = useHistory();
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const { trackingData } = useHomeownerAppContext();
  const availableNavLinks = jumpLinks.filter(link =>
    navItems.includes(link.name),
  );

  const [selectedNavButton, setSelectedNavButton] = useState<
    string | undefined
  >(location.hash.slice(1) || availableNavLinks[0]?.name);

  const handleNavButtonClick = ({
    path,
    name: navName,
    trackId,
  }: NavItemSchema) => {
    setSelectedNavButton(navName);
    history.push(path);
    buttonPressed({
      ...trackingData,
      button_text: navName,
      button_location: 'Project Jump List',
      primary_cta: false,
      project_id: projectId,
      track_id: trackId,
    });
    if (typeof onNavButtonClick === 'function') {
      onNavButtonClick(navName);
    }
  };

  return (
    <>
      <Box flexDirection="column" px={px} {...rest}>
        <Heading mb={100} size={100}>
          {t('projects.project')}
        </Heading>
        <Heading
          mb="200 !important"
          noOfLines={2}
          size={{ base: 600, tablet: 700 }}
        >
          {name}
        </Heading>
        {!!editedRegions.length && (
          <Body color="neutral.400" m={0} maxW={750} size={300}>
            {getAreaDescription(editedRegions, t)}
          </Body>
        )}
      </Box>
      {availableNavLinks.length ? (
        <Box
          alignItems="center"
          as="nav"
          background="neutral.0"
          flexDir="column"
          marginTop="200"
          mb="600"
          minW="100%"
          position="sticky"
          pt={{ base: '400', tablet: '600' }}
          px={px}
          ref={navRef}
          top={{ base: '80px', tablet: 0 }}
          zIndex="sticky"
        >
          <Box alignItems="flex-start" gap="600" width="100%">
            {availableNavLinks
              .filter(link => navItems.includes(link.name))
              .map(link => (
                <NavbarButton
                  capitalize={false}
                  color={
                    selectedNavButton === link.name
                      ? 'neutral.900'
                      : 'neutral.600'
                  }
                  isSelected={link.name === selectedNavButton}
                  key={link.name}
                  mode="light"
                  navbarItem={link}
                  onClick={() => handleNavButtonClick(link)}
                  padding={0}
                />
              ))}
          </Box>
          <Divider mb={0} />
        </Box>
      ) : undefined}
    </>
  );
};

export { ProjectDetails };
